/**
 * Funding envelopes — build 6B.
 *
 * The money available per fiscal year. The renewal forecast draws the capex
 * envelope beside each year's cost, and the deferred-maintenance curve says
 * what each funding level leaves unfunded; neither can say anything without
 * one. Someone holding `insight:write` sets a year (setting it again replaces
 * it), or removes it. Who set it and when is shown on the row.
 */
import { useState } from "react";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useEnvelopes, useRemoveEnvelope, useSetEnvelope } from "../../api/entryFormQueries";
import { useSession } from "../../app/SessionContext";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Skeleton,
  Table,
} from "../../design/components";
import { envelopeRefusal, refusalText, type EnvelopeKind, type FundingEnvelope } from "../../domain/entryForms";
import { t } from "../../i18n";
import { when } from "./KpiTargetsCard";
import s from "./EntryForms.module.css";

const fmt = (n: number, currency: string) => `${currency} ${n.toLocaleString("en", { maximumFractionDigits: 0 })}`;

export function EnvelopesCard() {
  const session = useSession();
  const canWrite = hasPermission(session.principal, PERMISSION.insightWrite);
  const q = useEnvelopes();
  const set = useSetEnvelope();
  const remove = useRemoveEnvelope();
  const year = new Date().getFullYear();
  const [fy, setFy] = useState(String(year + 1));
  const [kind, setKind] = useState<EnvelopeKind>("capex");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("SAR");
  const [label, setLabel] = useState("");
  const [why, setWhy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const edit = (e: FundingEnvelope) => {
    setFy(String(e.fiscalYear));
    setKind(e.kind);
    setAmount(String(e.amount));
    setCurrency(e.currency);
    setLabel(e.label ?? "");
    setWhy(null);
  };

  const save = async () => {
    const input = {
      fiscalYear: Number(fy),
      kind,
      amount: amount.trim() === "" ? Number.NaN : Number(amount),
      currency,
      label,
    };
    const refused = envelopeRefusal(input);
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      await set.mutateAsync(input);
      setSaved(
        t("{kind} envelope for {year} saved: {amount}.", {
          kind: kind === "capex" ? t("Capex") : t("Opex"),
          year: input.fiscalYear,
          amount: fmt(input.amount, input.currency.toUpperCase()),
        }),
      );
      setAmount("");
      setLabel("");
    } catch (e) {
      setWhy(refusalText(e));
    }
  };

  const drop = async (e: FundingEnvelope) => {
    setWhy(null);
    try {
      await remove.mutateAsync(e.id);
      setSaved(
        t("{kind} envelope for {year} removed.", {
          kind: e.kind === "capex" ? t("Capex") : t("Opex"),
          year: e.fiscalYear,
        }),
      );
    } catch (err) {
      setWhy(refusalText(err));
    }
  };

  return (
    <Card title="Funding envelopes" sub="Money available for each fiscal year." testId="am-envelopes">
      {q.isPending ? (
        <Skeleton rows={3} />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState>{t("No envelope is set, so no year can be called funded or short.")}</EmptyState>
      ) : (
        <div className={s.scroll}>
          <Table>
            <thead>
              <tr>
                <th>{t("Year")}</th>
                <th>{t("Kind")}</th>
                <th className={s.num}>{t("Amount")}</th>
                <th>{t("Basis")}</th>
                <th>{t("Set by")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(q.data ?? []).map((e) => (
                <tr key={e.id} data-testid={`envelope-${e.kind}-${e.fiscalYear}`}>
                  <td>{e.fiscalYear}</td>
                  <td>
                    {e.kind === "capex" ? t("Capex") : t("Opex")}
                    {e.scope === "tenant" && <span className={s.muted}> · {t("all sites")}</span>}
                    {!e.applies && <span className={s.muted}> · {t("not used: this site has its own")}</span>}
                  </td>
                  <td className={s.num}>{fmt(e.amount, e.currency)}</td>
                  <td className={s.muted}>{e.label ?? ""}</td>
                  <td className={s.muted}>{e.setBy ? `${e.setBy}, ${when(e.setAt)}` : ""}</td>
                  <td>
                    {canWrite && e.scope === "site" && (
                      <div className={s.actions}>
                        <Button size="sm" onClick={() => edit(e)}>
                          {t("Change")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void drop(e)} disabled={remove.isPending}>
                          {t("Remove")}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {canWrite ? (
        <div className={s.form} data-testid="envelope-form">
          <Field label="Fiscal year">
            <Input type="number" min={2000} max={2100} step={1} value={fy} onChange={(e) => setFy(e.target.value)} />
          </Field>
          <div>
            <Segmented
              ariaLabel="Envelope kind"
              value={kind}
              onChange={setKind}
              options={[
                { value: "capex", label: "Capex" },
                { value: "opex", label: "Opex" },
              ]}
            />
          </div>
          <Field label="Amount">
            <Input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field label="Currency">
            <Input value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
          </Field>
          <div className={s.wide}>
            <Field label="Basis — where the figure comes from">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("e.g. board-approved budget")}
                maxLength={200}
              />
            </Field>
          </div>
          {why && (
            <p className={`${s.err} ${s.wide}`} role="alert">
              {why}
            </p>
          )}
          {saved && (
            <p className={`${s.ok} ${s.wide}`} role="status">
              {saved}
            </p>
          )}
          <div className={`${s.actions} ${s.wide}`}>
            <Button variant="primary" onClick={() => void save()} disabled={set.isPending}>
              {set.isPending ? t("Saving…") : t("Save envelope")}
            </Button>
            <span className={s.muted}>{t("Saving a year again replaces its figure.")}</span>
          </div>
        </div>
      ) : (
        <p className={s.muted}>{t("Only an administrator or an accountable manager can set envelopes.")}</p>
      )}
    </Card>
  );
}
